import { useAuth0 } from '@auth0/auth0-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu';
import { useFeatureFlagIsEnabled } from '../../../../../infra/feature-flags/posthog/posthog-feature-flag-hook';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import BankCard from '../../../../../assets/healer-booking-session/BankCard.svg';
import Calendar from '../../../../../assets/healer-booking-session/Calendar.svg';
import DashboardIcon from '../../../../../assets/healer-booking-session/DashboardIcon.svg';
import SessionsIcon from '../../../../../assets/healer-booking-session/SessionsIcon.svg';
import UserIcon from '../../../../../assets/healer-booking-session/UserIcon.svg';
import UserProfile from '../../../../../assets/healer-booking-session/User_box_light.svg';
import Logo from '../../../../../assets/luminara-logo.svg';
import { logoutAsHealer } from '../../../../../core/usecases/healer-dashboard/logoutAsHealer';
import { navigateToAvailabilityAction } from '../../../../../core/usecases/side-navbar/navigateToAvailabilityAction';
import { navigateToHealerDashboardAction } from '../../../../../core/usecases/side-navbar/navigateToHealerDashboardAction';
import { navigateToHealerProfileAction } from '../../../../../core/usecases/side-navbar/navigateToHealerProfile';
import { navigateToHealerScheduleSessionAction } from '../../../../../core/usecases/side-navbar/navigateToHealerScheduleSessionAction';
import { navigateToHealerSessionsAction } from '../../../../../core/usecases/side-navbar/navigateToHealerSessions';
import { navigateToHealerSettingsAction } from '../../../../../core/usecases/side-navbar/navigateToHealerSettings';
import { selectHealerSideNavbarActiveTab } from '../../../../../store/selectors/global.selector';
import { resetHealerBookingInvitationLink } from '../../../../../store/selectors/schedule-booking-as-healer.selector';
import { navigateToHealerBankDetailsAction } from '../../../../../core/usecases/side-navbar/navigateToHealerBankDetails';

const HealerSideNavbar = () => {
  const activeTab = useSelector(selectHealerSideNavbarActiveTab);
  const isHealerScheduleSessionTabVisible = useFeatureFlagIsEnabled('HEALER_SCHEDULE_SESSION');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { logout, loginWithRedirect } = useAuth0();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleClickHome = () => {
    dispatch<any>(navigateToHealerDashboardAction(navigate));
    dispatch<any>(resetHealerBookingInvitationLink());
  };

  const handleClickScheduleSession = () => {
    dispatch<any>(navigateToHealerScheduleSessionAction(navigate));
    dispatch<any>(resetHealerBookingInvitationLink());
  };

  const handleClickAvailability = () => {
    dispatch<any>(navigateToAvailabilityAction(navigate));
    dispatch<any>(resetHealerBookingInvitationLink());
  };

  const handleClickSessions = () => {
    dispatch<any>(navigateToHealerSessionsAction(navigate));
    dispatch<any>(resetHealerBookingInvitationLink());
  };

  const handleClickBankDetails = () => {
    dispatch<any>(navigateToHealerBankDetailsAction(navigate));
    dispatch<any>(resetHealerBookingInvitationLink());
  };

  const handleClickMyProfile = () => {
    dispatch<any>(navigateToHealerProfileAction(navigate));
    dispatch<any>(resetHealerBookingInvitationLink());
  };

  const handleClickSettings = () => {
    dispatch<any>(navigateToHealerSettingsAction(navigate));
    dispatch<any>(resetHealerBookingInvitationLink());
    setMenuOpen(false);
  };

  const handleLogout = () => {
    dispatch<any>(logoutAsHealer(logout, loginWithRedirect));
  };

  return (
    <div className="bg-white/[0.4] fixed left-0 w-[15vw] min-w-[200px] min-h-screen flex flex-col justify-between select-none">
      <div>
        <div className="pl-6 pt-5 py-3 mb-5">
          <img src={Logo} alt="" className="select-none" />
        </div>

        <div className={`flex gap-3 px-5 py-3 mx-2 rounded-lg cursor-pointer ${activeTab === 'HOME' ? 'bg-white' : ''}`} onClick={handleClickHome}>
          <img src={DashboardIcon} alt="" />
          <p className="font-Sweet text-Body1">Home</p>
        </div>

        {isHealerScheduleSessionTabVisible && (
          <div
            className={`flex gap-3 px-5 py-3 mx-2 rounded-lg cursor-pointer ${activeTab === 'SCHEDULE_SESSION' ? 'bg-white' : ''}`}
            onClick={handleClickScheduleSession}>
            <img src={SessionsIcon} alt="" />
            <p className="font-Sweet text-Body1">Schedule session</p>
          </div>
        )}

        <div
          className={`flex gap-3 px-5 py-3 mx-2 rounded-lg cursor-pointer ${activeTab === 'HEALER_SESSIONS' ? 'bg-white' : ''}`}
          onClick={handleClickSessions}>
          <img src={SessionsIcon} alt="" />
          <p className="font-Sweet text-Body1">Sessions</p>
        </div>

        <div
          className={`flex gap-3 px-5 py-3 mx-2 rounded-lg cursor-pointer ${activeTab === 'AVAILABILITY' ? 'bg-white' : ''}`}
          onClick={handleClickAvailability}>
          <img src={Calendar} alt="" />
          <p className="font-Sweet text-Body1">Availability</p>
        </div>

        <div
          className={`flex gap-3 px-5 py-3 mx-2 rounded-lg cursor-pointer ${activeTab === 'BANK_DETAILS' ? 'bg-white' : ''}`}
          onClick={handleClickBankDetails}>
          <img src={BankCard} alt="" />
          <p className="font-Sweet text-Body1">Bank details</p>
        </div>
      </div>
      <div className="mb-10">
        <div className={`flex gap-3 px-5 py-3 mx-2 rounded-lg cursor-pointer ${activeTab === 'HEALER_PROFILE' && 'bg-white'}`} onClick={handleClickMyProfile}>
          <img src={UserProfile} alt="" />
          <p className="font-Sweet text-Body1">My profile</p>
        </div>
        <div className={`flex gap-3 h-[50px] px-5 mx-2 rounded-lg cursor-pointer ${activeTab === 'HEALER_SETTINGS' && 'bg-white'}`}>
          <DropdownMenu onOpenChange={(open) => setMenuOpen(open)} open={menuOpen}>
            <DropdownMenuTrigger className="flex gap-3 w-full h-full items-center outline-none">
              <img src={UserIcon} alt="" />
              <p className="font-Sweet text-Body1">Settings</p>
            </DropdownMenuTrigger>
            {menuOpen && (
              <DropdownMenuContent className="rounded-l w-auto flex flex-col justify-center" align="end" side="right" sideOffset={30}>
                <DropdownMenuLabel className="flex justify-center items-center bg-white h-[45px] w-[180px] rounded-t-lg" onClick={handleClickSettings}>
                  Account settings
                </DropdownMenuLabel>

                <DropdownMenuLabel className="flex justify-center items-center bg-white h-[45px] w-[180px] rounded-b-lg" onClick={handleLogout}>
                  Sign out
                </DropdownMenuLabel>
              </DropdownMenuContent>
            )}
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default HealerSideNavbar;
